import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Button, Card, useToast } from '@caruso/ui';
import { venuesService } from '@/services/venues.service';
import { Plus, Edit, Trash2, MapPin, Users, DollarSign, Calendar } from 'lucide-react';
import { cn } from '@caruso/ui';
import { Venue, PricingType } from '@caruso/types';

export function VenuesPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [showInactive, setShowInactive] = useState(false);

  const { data: venues, isLoading, refetch } = useQuery({
    queryKey: ['venues', showInactive],
    queryFn: () => venuesService.getAll(!showInactive),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => venuesService.delete(id),
    onSuccess: () => {
      toast({
        title: 'Ambiente removido',
        variant: 'success',
      });
      refetch();
    },
    onError: (error: any) => {
      toast({
        title: 'Erro ao remover ambiente',
        description: error?.response?.data?.message || 'Tente novamente',
        variant: 'destructive',
      });
    },
  });

  const handleDelete = async (id: string, name: string) => {
    if (confirm(`Tem certeza que deseja remover o ambiente "${name}"?`)) {
      deleteMutation.mutate(id);
    }
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(price);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-brand-gold">Ambientes</h1>
          <p className="text-brand-ivory/60 mt-1">
            Gerencie os espaços disponíveis para locação
          </p>
        </div>
        <Button onClick={() => navigate('/app/venues/new')} variant="premium">
          <Plus className="w-4 h-4 mr-2" />
          Novo Ambiente
        </Button>
      </div>

      {/* Filters */}
      <div className="flex gap-4">
        <Button
          variant={showInactive ? 'outline' : 'premium'}
          onClick={() => setShowInactive(false)}
        >
          Ativos
        </Button>
        <Button
          variant={showInactive ? 'premium' : 'outline'}
          onClick={() => setShowInactive(true)}
        >
          Todos
        </Button>
        <Button
          variant="outline"
          onClick={() => navigate('/app/venue-reservations/calendar')}
          className="ml-auto"
        >
          <Calendar className="w-4 h-4 mr-2" />
          Calendário
        </Button>
      </div>

      {/* Venues Grid */}
      {isLoading ? (
        <div className="text-center py-12 text-brand-ivory/50">
          <div className="animate-spin w-8 h-8 border-2 border-brand-gold border-t-transparent rounded-full mx-auto mb-4" />
          <p>Carregando ambientes...</p>
        </div>
      ) : venues && venues.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {venues.map((venue: Venue) => (
            <Card
              key={venue.id}
              className={cn(
                'bg-brand-ink/50 border-brand-gold/20 overflow-hidden hover:border-brand-gold/40 transition-colors',
                !venue.active && 'opacity-60',
              )}
            >
              {/* Imagem Principal */}
              <div className="relative h-48 bg-brand-ink/80">
                {venue.images && venue.images.length > 0 ? (
                  <img
                    src={venue.images.find(img => img.isPrimary)?.url || venue.images[0]?.url}
                    alt={venue.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <MapPin className="w-12 h-12 text-brand-gold/30" />
                  </div>
                )}
                {venue.featured && (
                  <div className="absolute top-2 right-2 bg-brand-gold text-brand-ink px-2 py-1 rounded text-xs font-bold">
                    DESTAQUE
                  </div>
                )}
                {!venue.active && (
                  <div className="absolute top-2 left-2 bg-red-500 text-white px-2 py-1 rounded text-xs font-bold">
                    INATIVO
                  </div>
                )}
              </div>

              {/* Conteúdo */}
              <div className="p-4 space-y-3">
                <div>
                  <h3 className="text-xl font-semibold text-brand-gold">
                    {venue.name}
                  </h3>
                  {venue.description && (
                    <p className="text-brand-ivory/60 text-sm mt-1 line-clamp-2">
                      {venue.description}
                    </p>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-2 text-sm">
                  {venue.capacity && (
                    <div className="flex items-center gap-2 text-brand-ivory/70">
                      <Users className="w-4 h-4" />
                      <span>{venue.capacity} pessoas</span>
                    </div>
                  )}
                  {venue.area && (
                    <div className="flex items-center gap-2 text-brand-ivory/70">
                      <MapPin className="w-4 h-4" />
                      <span>{venue.area}m²</span>
                    </div>
                  )}
                </div>

                <div className="flex items-baseline gap-2">
                  <DollarSign className="w-4 h-4 text-brand-gold" />
                  <span className="text-2xl font-bold text-brand-gold">
                    {formatPrice(venue.defaultPrice)}
                  </span>
                  <span className="text-brand-ivory/60 text-sm">
                    /{venue.pricingType === PricingType.DAILY ? 'dia' : 'hora'}
                  </span>
                </div>

                {venue._count && (
                  <div className="text-sm text-brand-ivory/60">
                    {venue._count.reservations} reserva(s) ativa(s)
                  </div>
                )}

                <div className="flex gap-2 pt-2 border-t border-brand-gold/10">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate(`/app/venues/${venue.id}`)}
                    className="flex-1"
                  >
                    <Edit className="w-4 h-4 mr-1" />
                    Editar
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDelete(venue.id, venue.name)}
                    className="text-red-400 hover:text-red-300 hover:border-red-400"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      ) : (
        <Card className="bg-brand-ink/50 border-brand-gold/20">
          <div className="text-center py-12 text-brand-ivory/50">
            <MapPin className="w-12 h-12 mx-auto mb-4 text-brand-gold/50" />
            <p className="text-lg font-medium mb-2">Nenhum ambiente cadastrado</p>
            <p className="text-sm mb-4">
              Comece cadastrando seu primeiro ambiente para locação
            </p>
            <Button onClick={() => navigate('/app/venues/new')} variant="premium">
              <Plus className="w-4 h-4 mr-2" />
              Cadastrar Primeiro Ambiente
            </Button>
          </div>
        </Card>
      )}
    </div>
  );
}
